import { useTheme, Box } from '@mui/material'
import { Scatter } from 'react-chartjs-2'
import { Chart as ChartJS, Plugin } from 'chart.js'
import { useTranslation } from 'react-i18next'
import { max, min } from 'lodash'
import { useAppSelector } from '../../store/hooks'
import { roundUpToNearest10 } from '../../shared/utils'
import { fsColors } from '../../shared/constants'

const ChartKPIB = ({
  results,
  height = 550,
  isMean = true,
}: {
  results: any
  height?: number
  isMean?: boolean
}) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const { currentLanguage } = useAppSelector((state) => state.currentUser)
  const labels = results.map((r: any) => t(r.label.text))
  const values = results.map((r: any) => ({ x: Number(r.value), y: t(r.label.text) }))

  const drawHorizontalLinesPlugin: Plugin<'scatter'> = {
    id: 'horizontalLinesPlugin',
    afterDatasetsDraw(chart) {
      const { ctx, data, scales } = chart

      const xZero = scales.xBottom.getPixelForValue(0)

      ctx.save()
      ctx.lineWidth = 1

      data.datasets.forEach((dataset) => {
        dataset.data.forEach((point) => {
          if (point && typeof point === 'object' && 'x' in point && 'y' in point) {
            const x = scales.xBottom.getPixelForValue(point.x)
            const y = scales.y.getPixelForValue(point.y)
            ctx.strokeStyle =
              point.x >= 0 ? fsColors.fsDarkBlue.colors[1] : fsColors.fsGray.colors[1]

            ctx.beginPath()
            ctx.moveTo(xZero, y)
            ctx.lineTo(x, y)
            ctx.stroke()
          }
        })
      })

      ctx.restore()
    },
  }

  return (
    <Box py={2} height={height}>
      <Scatter
        className="variable_mean_pay_gap"
        id="variable_mean_pay_gap"
        data={{
          labels,
          datasets: [
            {
              data: values,
              backgroundColor: (context) => {
                // @ts-ignore
                return context.raw?.x >= 0
                  ? fsColors.fsDarkBlue.colors[1]
                  : fsColors.fsGray.colors[1]
              },
              pointRadius: 8,
              pointHoverRadius: 8,
              xAxisID: 'xBottom',
            },
          ],
        }}
        options={{
          maintainAspectRatio: false,
          indexAxis: 'y',
          layout: {
            padding: {
              top: 10,
              right: 50,
              left: 25,
            },
          },
          plugins: {
            // @ts-ignore
            legendMargin: false,
            legend: {
              display: false,
            },
            tooltip: {
              enabled: true,
              callbacks: {
                label: (context) => {
                  return `${t('gender.pay.gap.company')} (${
                    isMean ? t('mean.value') : t('median.value')
                    // @ts-ignore
                  }): ${context.raw?.x.toLocaleString(currentLanguage, {
                    maximumFractionDigits: 2,
                  })} %`
                },
              },
            },
            datalabels: {
              anchor: 'end',
              align: 'end',
              color: theme.palette.text.primary,
              formatter: (value) =>
                `${Number(value.x).toLocaleString(currentLanguage, {
                  maximumFractionDigits: 2,
                })} %`,
            },
          },
          scales: {
            x: {
              display: false,
            },
            xBottom: {
              title: {
                display: false,
              },
              type: 'linear',
              offset: true,
              min: roundUpToNearest10(Number(min(values.map((v: any) => v.x)) ?? 0) - 10),
              max: roundUpToNearest10(Number(max(values.map((v: any) => v.x)) ?? 0) + 10),
              grid: {
                display: true,
                color: theme.palette.divider,
              },
              border: {
                color: theme.palette.text.disabled,
              },
              ticks: {
                autoSkip: true,
                stepSize: 10,
                maxTicksLimit: 8,
                color: theme.palette.text.secondary,
                callback: (value) =>
                  Number(value) % 5 === 0
                    ? `${Number(value).toLocaleString(currentLanguage, {
                        maximumFractionDigits: 2,
                      })} %`
                    : value,
              },
            },
            xTop: {
              position: 'top',
              type: 'linear',
              offset: true,
              min: roundUpToNearest10(Number(min(values.map((v: any) => v.x)) ?? 0) - 10),
              max: roundUpToNearest10(Number(max(values.map((v: any) => v.x)) ?? 0) + 10),
              grid: {
                display: false,
              },
              border: {
                display: false,
              },
              ticks: {
                autoSkip: true,
                stepSize: 10,
                maxTicksLimit: 8,
                color: theme.palette.text.secondary,
                callback: (value) =>
                  Number(value) % 5 === 0
                    ? `${Number(value).toLocaleString(currentLanguage, {
                        maximumFractionDigits: 2,
                      })} %`
                    : value,
              },
            },
            y: {
              title: {
                display: false,
              },
              grid: {
                display: false,
                color: theme.palette.divider,
              },
              border: {
                color: theme.palette.text.disabled,
              },
              ticks: {
                color: theme.palette.text.primary,
                padding: 20,
                font: {
                  weight: 'bold',
                },
              },
              offset: true,
              type: 'category',
            },
          },
        }}
        plugins={[drawHorizontalLinesPlugin]}
      />
    </Box>
  )
}

export default ChartKPIB
